import {
  isTenantAssetContentType,
  MAX_TENANT_ASSET_BYTES,
  type TenantAssetContentType,
} from "@loot-os/api-contract";

/** What the drop zone accepts, the same four rasters the server's sniff allows. */
export const BOX_IMAGE_ACCEPT =
  ".png,.jpg,.jpeg,.webp,.avif,image/png,image/jpeg,image/webp,image/avif";

/** The refusal for a file that is not one of the four accepted image types. */
export const IMAGE_TYPE_REFUSAL = "Use a PNG, JPEG, WEBP or AVIF image.";

/** The server's upload start answer: the key to finish with, and where to PUT the file. */
interface ImageUploadTicket {
  uploadKey: string;
  url: string;
  headers: Record<string, string>;
}

const CONTENT_TYPE_BY_EXTENSION: Record<string, TenantAssetContentType> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  avif: "image/avif",
};

/**
 * Uploads one picked image the way the dashboard does: `start` signs a PUT for its type and
 * size, the browser sends the bytes straight to the bucket, and `finish` has the server check
 * what landed and answer the public URL. The type and size are checked here first so the drop
 * zone refuses at once; the server checks both again.
 */
export async function uploadBoxImage(
  file: File,
  start: (input: {
    contentType: TenantAssetContentType;
    byteLength: number;
  }) => Promise<ImageUploadTicket>,
  finish: (uploadKey: string) => Promise<{ publicUrl: string }>,
): Promise<string> {
  const contentType = requireImageContentType(file);
  if (file.size > MAX_TENANT_ASSET_BYTES) {
    throw new Error(
      `Use an image smaller than ${Math.floor(MAX_TENANT_ASSET_BYTES / 1024 / 1024)} MB.`,
    );
  }
  const ticket = await start({ contentType, byteLength: file.size });
  const put = await fetch(ticket.url, { method: "PUT", headers: ticket.headers, body: file });
  if (!put.ok) throw new Error("The image could not be uploaded. Try again.");
  return (await finish(ticket.uploadKey)).publicUrl;
}

/** The file's type as the browser reports it, else from its extension (some report AVIF as ""). */
function requireImageContentType(file: File): TenantAssetContentType {
  if (isTenantAssetContentType(file.type)) return file.type;
  const extension = /\.([a-z0-9]+)$/i.exec(file.name)?.[1]?.toLowerCase();
  const fromName = extension ? CONTENT_TYPE_BY_EXTENSION[extension] : undefined;
  if (file.type === "" && fromName) return fromName;
  throw new Error(IMAGE_TYPE_REFUSAL);
}
